import algoliasearch, {SearchClient, SearchIndex} from 'algoliasearch';
import {SearchOptions, SearchResponse, Settings} from '@algolia/client-search';
import aa from 'search-insights';

import {getConfig} from 'coreUtils/utils';

import {
	ProductSearchHit,
	ProductSearchParams,
	ProductSearchRefinement,
	ProductSearchRefinementValue,
	ProductSearchResultState as ProductSearchResultType,
} from 'coreStore/data/productSearchResult/interface';

import ManagerRepository from 'brandClasses/ManagerRepository';
import {DEFAULT_SEARCH_PARAMS, PERSONALIZATION_SEARCH_REFINEMENT, REFINEMENT_MAP_BY_PROVIDERS} from 'brandConstants';

import {
	ESearchProvider,
	ESearchType,
	IPopularSearchesParams,
	ISearchOptions,
	ISearchParams,
	ISearchRefinements,
	ISearchSuggestionsParams
} from '../../ISearch';
import SearchModel from '../../SearchModel';
import AbstractSearchProvider from '../AbstractSearchProvider';
import {
	AlgoliaHit,
	AlgoliaSuggestionHit,
	IAlgoliaRefinementConfig,
	IAlgoliaSearchRefinementBucket,
	IAlgoliaSortingConfig,
	IAlgoliaTranslationsConfig
} from './IAlgoliaAPI';

import {EInstanceKey} from 'coreStore/data/ui/types';

const preferences = {
	sorting: 'sitePreferences.Algolia_Sorting',
	refinements: 'sitePreferences.Algolia_Refinements',
	appID: 'sitePreferences.Algolia_ApplicationID',
	apiKey: 'sitePreferences.Algolia_SearchApiKey',
	translations: 'sitePreferences.Algolia_Translations',
	customerGroups: 'sitePreferences.Algolia_CustomerGroups',
	searchRefinements: 'sitePreferences.Algolia_SearchRefinements'
};

const MAX_CONTEXTS = 10;

/**
 * Represents AlgoliaSearchProvider
 */
class AlgoliaSearchProvider extends AbstractSearchProvider {
	private DEFAULT_LANGUAGE = 'en';
	private algoliaClient: SearchClient;
	private refinementsConfig: IAlgoliaRefinementConfig[];
	private sortingConfig: IAlgoliaSortingConfig[];
	private sortingOptions: ProductSearchResultType['sortingOptions'];
	private customerGroups: string[];
	private searchRefinementsConfig: Record<string, IAlgoliaSearchRefinementBucket[]> = {};
	readonly baseIndexName: string;
	readonly facets: string[];
	readonly translations: IAlgoliaTranslationsConfig;
	readonly categoryAttribute: string;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {string} key - key
	 * @param {string} cacheKey - search instance cache key ( used in redux and class cache )
	 * @param {ESearchType} searchType - search type
	 */
	constructor(managerRepository: ManagerRepository, key: string, cacheKey: string, searchType: ESearchType) {
		super(managerRepository, key, cacheKey, searchType);

		const configurationMgr = this.managerRepository.getConfigurationsMgr();

		this.refinementsConfig = (
			configurationMgr.get(preferences.refinements) || []
		) as IAlgoliaRefinementConfig[];

		this.refinementsConfig = this.refinementsConfig.filter((refinement) => {
			if (this.searchType === ESearchType.QUICK_SEARCH && refinement.enableOnQuickSearch === false) {
				return false;
			}

			return !(this.searchType === ESearchType.PAGE && refinement.enableOnPlp === false);
		});

		this.searchRefinementsConfig = configurationMgr.get(preferences.searchRefinements) || {};

		this.sortingConfig = (
			configurationMgr.get(preferences.sorting) || []
		) as IAlgoliaSortingConfig[];

		this.sortingConfig = this.sortingConfig.filter((sorting) => {
			if (this.searchType === ESearchType.QUICK_SEARCH && sorting.enableOnQuickSearch === false) {
				return false;
			}

			return !(this.searchType === ESearchType.PAGE && sorting.enableOnPlp === false);
		});

		this.translations = (
			configurationMgr.get(preferences.translations) || {}
		) as IAlgoliaTranslationsConfig;

		this.facets = this.refinementsConfig.reduce((acc, curr) => {
			if (curr.type === 'action') {
				return acc;
			}

			acc.push(curr.attribute);

			return acc;
		}, []);

		this.sortingOptions = this.sortingConfig.map(sort => ({
			id: sort.id,
			label: sort.label,
		}));

		this.customerGroups = (configurationMgr.get(preferences.customerGroups) || '')
			.split(',')
			.map((el: string) => el.trim());

		this.algoliaClient = algoliasearch(
			configurationMgr.get(preferences.appID),
			configurationMgr.get(preferences.apiKey)
		);

		const config = getConfig();

		let hostName = config.app.sfccHostname;

		// In development or sandbox mode, we need to remove the domain from the hostname
		if (
			managerRepository.getUiMgr().getInstanceKey() === EInstanceKey.DEVELOP ||
			managerRepository.getUiMgr().getInstanceKey() === EInstanceKey.SANDBOX
		) {
			hostName = hostName.replace('.demandware.net', '');
			hostName = hostName.replace('.commercecloud.salesforce.com', '');
		}

		this.baseIndexName = hostName.replace(/[.|-]/g, '_');

		this.categoryAttribute = REFINEMENT_MAP_BY_PROVIDERS?.[ESearchProvider.ALGOLIA]?.category || null;
	}

	/**
	 * Retrieves the site ID for Algolia search.
	 *
	 * @returns The site ID for Algolia search.
	 */
	get siteId() {
		const localeMgr = this.managerRepository.getLocalesMgr();
		const siteId = localeMgr.getCurrentCountry().getSiteId() || getConfig().app.sfccAPI.siteId;

		return siteId ;
	}

	/**
	 * Returns the current language code
	 *
	 * @returns {string} current language code
	 */
	get currentLanguageCode(): string {
		const localeMgr = this.managerRepository.getLocalesMgr();

		return localeMgr.getCurrentLanguage()?.getLanguageCode() || this.DEFAULT_LANGUAGE;
	}

	/**
	 * Returns the base index name for general use
	 *
	 * @returns {string} index name
	 */
	getBaseIndexName(): string {
		return `${this.baseIndexName}__${this.siteId}__products__${this.currentLanguageCode}`;
	}

	/**
	 * Returns the Algolia index
	 *
	 * @returns {SearchIndex} Algolia index
	 */
	getIndex(query: ProductSearchParams): SearchIndex {
		let indexName = this.getBaseIndexName();

		if (query.sort) {
			const sortingConfig = this.sortingConfig.find((sort) => sort.id === query.sort);

			if (sortingConfig) {
				indexName = `${indexName}${sortingConfig.attribute}`;
			}
		}

		return this.algoliaClient.initIndex(indexName);
	}

	/**
	 * Returns the Algolia index
	 *
	 * @returns {SearchIndex} Algolia index for suggestions
	 */
	getIndexSuggestions(): SearchIndex {
		let indexName = this.getBaseIndexName();
		indexName = `${indexName}_query_suggestions`;

		return this.algoliaClient.initIndex(indexName);
	}

	/**
	 * Returns refinements map
	 * @param {string[]} refine - attribute IDs
	 * @param {object} opts - remove refinements selected by user
	 * @returns {string}
	 */
	private createFilters(refine: string[], opts: {excludeRefinements?: string[]} = {}): string[] {
		const {excludeRefinements = []} = opts;

		return refine.reduce((acc, refine) => {
			const [attributeId, valuesRaw] = refine.split('=');

			if (!valuesRaw) {
				return acc;
			}

			if (excludeRefinements.includes(attributeId)) {
				return acc;
			}

			const values = valuesRaw.split('|');

			if (!values.length) {
				return acc;
			}

			const data = values.map((value) => `${attributeId}:${value}`);

			acc.push(data);

			return acc;
		}, []);
	}

	/**
	 * Creates an array of refinement parameter strings.
	 *  @param {Record<string, any>} refinements - object representing the refinements
	 *  @returns {string[]} - an array of strings representing the refinement parameters
	 */
	private createRefineParam(refinements: Record<string, any>): string[] {
		return Object.keys(refinements || {})
			.map((key) => {
				const attributeID = this.refinementsConfig.find((el) => el.id === key)?.attribute || key;

				return `${attributeID}=${(refinements[key] as string[]).join('|')}`;
			});
	}

	/**
	 * Transforms the facets object into an array of ProductSearchRefinement objects
	 *  @param {Record<string, Record<string, number>>} facets - facets
	 *  @param {Settings['renderingContent']} renderingContent - rendering content
	 *  @returns {ProductSearchRefinement[]} - an array of strings representing the refinement parameters
	 */
	private mapFacets(
		facets: Record<string, Record<string, number>>,
		renderingContent: Settings['renderingContent']
	): ProductSearchRefinement[] {
		return this.refinementsConfig.reduce((acc, refinementConfig) => {
			const {attribute, id} = refinementConfig;
			const facetField = facets[attribute];

			if (!facetField) {
				return acc;
			}

			let label = refinementConfig.label;

			if (this.translations?.[id]?.[this.currentLanguageCode]) {
				label = this.translations[id][this.currentLanguageCode];
			}

			const values: ProductSearchRefinementValue[] =
				Object
					.entries(facetField)
					.reduce((acc, [value, count]) => {
						const orderedFacetField = renderingContent?.facetOrdering?.values[attribute];
						let orderIndex: number;

						if (orderedFacetField && orderedFacetField.order) {
							orderIndex = orderedFacetField.order.length;
							orderedFacetField.order.forEach((order, index) => {
								if (value === order) {
									orderIndex = index;
								}
							});
						}

						if (orderedFacetField?.sortRemainingBy === 'hidden') {
							if (orderedFacetField.order.includes(value)) {
								const presentationId = this.findPresentationId(attribute, value);
								acc.push({
									hitCount: count,
									value,
									presentationId,
									label: value,
									order: orderIndex
								});
							}

							return acc;
						}

						const presentationId = this.findPresentationId(attribute, value);
						acc.push({
							hitCount: count,
							value,
							presentationId,
							label: value,
							order: orderIndex
						});

						return acc;
					}, [])
					.sort((a, b) => a.order - b.order);

			const refinement = {
				attributeId: id,
				label,
				values,
			};

			acc.push(refinement);

			return acc;
		}, []);
	}

	/**
	 * Finds the presentation ID for a given attribute and value
	 * @param {string} attribute - attribute name
	 * @param {string} value - attribute value
	 * @returns {string} presentation ID or original value if not found
	 */
	private findPresentationId(attribute: string, value: string): string {
		const attributeConfig = this.searchRefinementsConfig?.[attribute];

		if (!attributeConfig || !Array.isArray(attributeConfig)) {
			return value;
		}

		const matchingItem = attributeConfig.find(item => {
			if (!item.bucketValues) {
				return false;
			}

			const bucketValues = item.bucketValues.split(',').map(v => v.trim());

			return bucketValues.some(bucketValue =>
				bucketValue.toLowerCase() === value.toLowerCase()
			);
		});

		return matchingItem?.presentationId || value;
	}

	/**
	 * Fetches the facets based on the given query
	 * @param {Object} query - search query
	 * @param {ISearchRefinements} userSelectedRefinements - refinements selected by the customer
	 * @returns {Promise<ProductSearchRefinement[]>}
	 */
	private async fetchFacets(
		query: ProductSearchParams,
		userSelectedRefinements: ISearchRefinements = {}
	): Promise<{
		facets: Record<string, Record<string, number>>,
		renderingContent: Settings['renderingContent']
	}> {
		// Here, we don't need to check if refinements are inside blacklistPlp or whiteListPlp
		// because userSelectedRefinement comes from refinements that have already been filtered.
		const refinements = Object.keys(userSelectedRefinements).reduce((acc, attributeId) => {
			const refine = this.refinementsConfig.find((el) => el.id === attributeId);

			if (refine) {
				acc.push(refine.attribute);
			}

			return acc;
		}, []);

		if (!refinements.length) {
			return {
				facets: {},
				renderingContent: {}
			};
		}

		const queries = refinements.map((attributeID) => {
			const filters = this.createFilters(query.refine, {excludeRefinements: [attributeID]});
			const searchOptions: SearchOptions = {
				hitsPerPage: 0,
				page: 0,
				facetFilters: filters,
				facets: [attributeID],
			};

			return {
				indexName: this.getBaseIndexName(),
				params: searchOptions,
				query: query.q
			};
		});

		const {results} = await this.algoliaClient.search(queries) as {results: SearchResponse[]};

		return results.reduce<{
			facets: Record<string, Record<string, number>>,
			renderingContent: Settings['renderingContent']
		}>((acc, {facets, renderingContent}) => {
			return {
				facets: {
					...acc?.facets,
					...facets,
				},
				renderingContent: {
					facetOrdering: {
						values: {
							...acc?.renderingContent?.facetOrdering?.values,
							...renderingContent?.facetOrdering?.values,
						}
					}
				},
			};
		}, null);
	}

	/**
	 * Returns the facets filtered for the current category
	 * @param {ISearchRefinements} searchRefinements - refinements
	 * @returns {string[]}
	 */
	private getFacets(searchRefinements?: ISearchRefinements): string[] {
		if (
			this.searchType !== ESearchType.PAGE ||
			!this.categoryAttribute ||
			!searchRefinements[this.categoryAttribute]?.[0]
		) {
			return this.facets;
		}

		const currentCategory = searchRefinements[this.categoryAttribute]?.[0];

		return this.facets.filter((facet) => {
			const refine = this.refinementsConfig.find((el) => el.attribute === facet);

			if (Array.isArray(refine.blacklistPlp) && refine.blacklistPlp.includes(currentCategory)) {
				return false;
			}

			return !(Array.isArray(refine.whitelistPlp) && !refine.whitelistPlp.includes(currentCategory));
		});
	}

	/**
	 * Translates the sorting options
	 *
	 * @returns {ProductSearchResultType['sortingOptions']} - an array of sorting options
	 */
	private translateSortingOptions(): ProductSearchResultType['sortingOptions'] {
		return this.sortingOptions.map((sort) => {
			let label = sort.label;

			if (this.translations?.[sort.id]?.[this.currentLanguageCode]) {
				label = this.translations?.[sort.id]?.[this.currentLanguageCode];
			}

			return {
				id: sort.id,
				label,
			};
		});
	}

	/**
	 * Executes API call to get the ProductSearchResultType based on given query
	 * @param {Object} query parameters for search
	 * @param {Object} searchRefinements parameters for search
	 * @param {Object} isBot parameters for search
	 * @returns Promise<ProductSearchResultType>
	 */
	async fetchSearch(
		query: ProductSearchParams,
		searchRefinements?: ISearchRefinements,
		isBot?: boolean
	): Promise<ProductSearchResultType> {
		const filters = this.createFilters(query.refine);
		const currentCustomer = this.managerRepository.getCustomerMgr().getCurrentCustomer();
		const currentCountry = this.managerRepository.getLocalesMgr().getCurrentCountry();
		const enableABTest = !!(!isBot && currentCustomer.usid);
		const filteredCustomerGroups = this.customerGroups.filter((customerGroup) => (
			currentCustomer.customerGroups.find(item => item.ID === customerGroup)
		));
		const ruleContexts = [currentCountry.getCountryCode(), ...filteredCustomerGroups].slice(0, MAX_CONTEXTS);

		const searchOptions: SearchOptions = {
			hitsPerPage: query.limit,
			page: Math.floor(query.offset / (query.limit !== 0 ? query.limit : 1)),
			facetFilters: filters,
			facets: this.getFacets(searchRefinements),
			clickAnalytics: true,
			userToken: currentCustomer.usid,
			ruleContexts,
			enableABTest,
		};

		const currency = currentCountry.getCurrencyCode();
		const priceBooksGroup = currentCountry.getPriceBooksGroup();

		aa('setUserToken', currentCustomer.usid);

		const [
			{
				hits,
				nbHits,
				nbSortedHits,
				queryID,
				facets: searchFacets,
				renderingContent: searchRenderingContent,
			},
			otherFacets
		] = await Promise.all([
			this.getIndex(query).search<AlgoliaHit>(query.q, searchOptions),
			this.fetchFacets(query, searchRefinements)
		]);
		const redirectContent = (searchRenderingContent as {redirect?: unknown})?.redirect;

		const facets = {
			...searchFacets,
			...otherFacets.facets,
		};
		const renderingContent = {
			facetOrdering: {
				facets: searchRenderingContent?.facetOrdering?.facets,
				values: {
					...searchRenderingContent?.facetOrdering?.values,
					...otherFacets?.renderingContent?.facetOrdering?.values,
				}
			}
		};

		const refinements = this.mapFacets(facets, renderingContent);

		const productHits: Array<ProductSearchHit> = hits.map((hit) => {
			const image = hit.image_groups?.[0]?.images?.[0] || null;
			const thronAssetId = hit.c_thronAssetsIds?.[0] || null;
			const pricing = hit.pricing || {};
			const price = pricing[priceBooksGroup]?.listPrice || null;

			return {
				c_url: hit.url,
				currency,
				hitType: 'variations-group',
				image: image ? {
					alt: image.alt,
					disBaseLink: image.dis_base_link,
					link: image.dis_base_link,
					title: image.title,
				} : null,
				orderable: hit.in_stock,
				price,
				productId: hit.id,
				productName: hit.name,
				productType: {master: false},
				representedProduct: {id: hit.defaultVariantID},
				representedProducts: (hit.variants || []).map((variant) => ({id: variant.variantID})),
				c_thronAssetsIds: thronAssetId ? [thronAssetId] : null,
			};
		});

		return {
			hits: productHits,
			limit: query.limit,
			offset: query.offset,
			query: query.q,
			queryKey: this.getKeyByQuery(query),
			queryId: queryID,
			searchPhraseSuggestions: {
				phrase: query.q
			},
			total: nbSortedHits || nbHits,
			refinements,
			sortingOptions: this.translateSortingOptions(),
			productSearchParams: query,
			redirectUrl: (redirectContent as {url?: string} | undefined)?.url,
		} as ProductSearchResultType;
	}

	/**
	 * Returns the SearchModel based on the given parameters
	 * If the SearchModel is not in store, it is fetched, stored and then returned
	 *
	 * @param {ISearchParams} params Search Params
	 * @param {ISearchOptions} options Search Options
	 * @returns {Promise<SearchModel>}
	 */
	public async search(params: ISearchParams = {}, options: ISearchOptions = {}): Promise<SearchModel> {
		const {
			refinements = this.userSelectedRefinements,
			sortingRule = this.userSelectedSorting,
			searchQuery = this.searchQuery,
			page = this.userSelectedPage,
			limitParam = this.limit,
		} = params;
		const {
			isBot
		} = options;
		const customer = this.managerRepository.getCustomerMgr().getCurrentCustomer();
		const customerPersonalizationKey = customer.getCustomerPersonalizationKey();
		const query: ProductSearchParams = {
			q: '',
			refine: [],
			sort: '',
			offset: 0,
			limit: 0
		};

		if (customerPersonalizationKey) {
			const key = PERSONALIZATION_SEARCH_REFINEMENT.replace('c_', '');
			refinements[key] = [customerPersonalizationKey];
		}

		refinements['online'] = ['true'];

		query.refine = this.createRefineParam(refinements);
		query.sort = sortingRule || '';

		const limit = limitParam || DEFAULT_SEARCH_PARAMS.limit;
		this.setLimit(limit);
		query.limit = limit;
		query.offset = (page - 1) * limit;

		if (searchQuery) {
			query.q = searchQuery;
		}

		try {
			const searchResult = await this.fetchSearch(query, refinements, isBot);

			searchResult.selectedSortingOption = sortingRule;
			searchResult.selectedRefinements = {};
			Object.keys(refinements).forEach(key => {
				searchResult.selectedRefinements[key] = (refinements[key] as string[]).join('|');
			});

			this.setSearchQuery(searchQuery);
			this.storeSearch(searchResult, searchResult.queryKey);
		} catch (e) {
			console.error("[Algolia] Search error, probably some indexes doesn't exists.", e);
		}

		return this.getStoredSearchModel();
	}

	/**
	 * Returns the suggestions based on the given parameters
	 *
	 * @param {ISearchParams} params Search Params
	 * @returns {Promise<string[]>}
	 */
	public async suggestions(params?: ISearchSuggestionsParams): Promise<string[]> {
		const {searchQuery, limit = 5} = params;

		if (!searchQuery) {
			return [];
		}

		try {
			const {hits} = await this.getIndexSuggestions().search<AlgoliaSuggestionHit>(searchQuery, {
				hitsPerPage: limit,
				page: 0,
			});

			return hits.map(hit => hit.query);
		} catch (e) {
			console.error('Algolia search suggestions error:', e);

			return [];
		}
	}

	/**
	 * Returns the suggestions based on the given parameters
	 *
	 * @param {ISearchParams} params Search Params
	 * @returns {Promise<string[]>}
	 */
	public async popularSearches(params?: IPopularSearchesParams): Promise<string[]> {
		const {limit = 3} = params;

		try {
			const {hits} = await this.getIndexSuggestions().search<AlgoliaSuggestionHit>('', {
				hitsPerPage: limit,
				page: 0,
			});

			return hits.map(hit => hit.query);
		} catch (e) {
			console.error('Algolia popular search error:', e);

			return [];
		}
	}

	/**
	 * Translate refinements when change language
	 *
	 * @returns {Promise<void>}
	 */
	public async translateRefinements(): Promise<void> {
		this.clearRefinements();
		await this.search();

		return Promise.resolve();
	}

	/**
	 * Translates Refinements
	 * @returns {Promise<void>}
	 */
	public translateSelectedRefinements(): Promise<void> {
		return Promise.resolve();
	}

	/**
	 * Translate refinements and url when
	 * redirect in a different language triggers
	 *
	 * @param {string | undefined} cgid category id
	 * @param {object} refinements current search refinements
	 * @param {string | undefined} searchQuery search query
	 * @param {string} languageCodeFromUrl language code in the url
	 * @param {string} currentLanguageCode current language code
	 *
	 * @returns Promise<void>
	 */
	public handleSearchRedirectInAnotherLang(
		_cgid: string | undefined,
		_refinements: object,
		_searchQuery: string | undefined,
		_languageCodeFromUrl: string,
		_currentLanguageCode: string
	): Promise<void> {
		return Promise.resolve();
	}

	/**
	 * Returns the type of the search provider.
	 *
	 * @returns {ESearchProvider} The enum value representing the Algolia search provider.
	 */
	getProviderType(): ESearchProvider {
		return ESearchProvider.ALGOLIA;
	}
}

export default AlgoliaSearchProvider;
